import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { ContactPointCodes, RelationshipCodes } from '../../../../../lib/Codes'
import http from '../../../../../lib/http'
import { capitalize } from '../../../../igao/utils'
import { FriendlyDateTime } from '../../../../shared/Date/friendly-date-time'
import { NoDataPlaceHolder } from '../../../behavioral/components'

interface InviteHistoryResponse {
  id: string
  sent_by_name: string
  created_at: string
  phone_number: string
  patient_name: string
  platform: string
  relationship: string | null
  phone_type: string | null
  receiver_name: string | null
}


function useInvites (id: string) {
  return useQuery({
    queryKey: [ 'mobile_invited', id ],
    queryFn: async () => {
      return await http.getRequest<InviteHistoryResponse[]>(`/patients/${id}/mobile-app-invitation`)
    }
  })
}

export function InviteHistory (): JSX.Element {
  const [ open, setOpen ] = useState<boolean>(true)
  const params = useParams()
  const { data } = useInvites(params?.id ?? '')

  return (
    <div className='nh-card nh-invite-card mb-16 mt-8'>
      <div className='nh-invite-head'>
        <button
          className='nh-invite-left'
          onClick={() => setOpen((prev) => !prev)}
        >
          <span className='nh-chevron-wrap'>
            <i
              className='fa fa-chevron-right nh-chevron'
              aria-hidden='true'
            />
          </span>
          <h3 className='nh-invite-title'>Invitation History</h3>
        </button>
      </div>

      <div
        id='invite-history-body'
        className={`nh-invite-body ${open ? 'open' : 'collapsed'}`}
      >
        <table className='accordionTable'>
          <thead>
            <tr>
              <th className='not-sortable'>Date/Time</th>
              <th className='not-sortable'>Recipient</th>
              <th className='not-sortable'>Phone Number</th>
              <th className='not-sortable'>Platform</th>
              <th className='not-sortable'>Sent By</th>
            </tr>
          </thead>

          <tbody>
            {!!data?.length && data?.map((r) => {
              let relationship
              let phoneType = ContactPointCodes.contactPointUse.find((cpu) => cpu.code === r.phone_type)?.label

              if (r.relationship === 'patient') {
                relationship = 'Patient'
              } else {
                relationship = capitalize(RelationshipCodes.find((rc: {
                  value: string
                }) => rc.value === r.relationship)?.display)
              }

              return (
                <tr
                  className='data-row'
                  key={r.id}
                >
                  <td><FriendlyDateTime datetime={r.created_at} /></td>
                  <td>
                    <span>
                      {r.receiver_name}{' '}
                      <span
                        className='text-zinc-500 text-xs font-bold'
                      >
                        {relationship}
                      </span>
                    </span>
                  </td>
                  <td>
                    <span>
                      {r.phone_number}{' '}
                      <span
                        className='text-zinc-500 text-xs font-bold'
                      >
                        {phoneType}
                      </span>
                    </span>
                  </td>
                  <td>
                    {r.platform === 'android'
                      ? <span className='nh-platform android'><i className='fa fa-android' /> Android</span>
                      : <span className='nh-platform ios'><i className='fa fa-apple' />{r.platform}</span>
                    }
                  </td>
                  <td>{r.sent_by_name}</td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {!data?.length && (
          <div>
            <NoDataPlaceHolder title='There is no Invitation History to display' />
          </div>
        )}

        <div className='nh-invite-footnote'>
          Note: The system tracks invitation attempts but does not verify SMS delivery to avoid
          additional messaging costs.
        </div>
      </div>
    </div>
  )
}
